/**
 * @fileoverview API service for doctor availability management
 */

import http from './http';
import { formatISTTimeToUTC, formatUTCToISTTime } from '../utils/timezone';

/**
 * Create availability block for a doctor
 * @param {string|Object} doctorIdOrObject - Doctor ID string or doctor object with _id/id
 * @param {Object} data - Availability block data
 * @returns {Promise<Object>} Created availability block
 */
export const createAvailabilityBlock = async (doctorIdOrObject, data) => {
  try {
    // Extract doctor ID from object or use as string
    const doctorId = typeof doctorIdOrObject === 'object' && doctorIdOrObject !== null
      ? (doctorIdOrObject._id || doctorIdOrObject.id)
      : doctorIdOrObject;

    if (!doctorId) {
      throw new Error('Doctor ID is required');
    }

    // Ensure the payload matches the backend API structure
    const payload = {
      date: data.date,
      startTime: data.startTime,
      endTime: data.endTime,
      slotMinutes: Number(data.slotMinutes) || 30,
      bufferMinutes: Number(data.bufferMinutes) || 0
    };

    console.log('Creating availability block with payload:', payload);

    const response = await http.post(`/api/v1/doctors/${doctorId}/availability-blocks`, payload);
    
    return {
      success: true,
      data: response.data,
      message: 'Availability block created successfully'
    };
  } catch (error) {
    console.error('Error creating availability block:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Failed to create availability block'
    };
  }
};

/**
 * Get doctor availability for a specific date or date range
 * @param {string|Object} doctorIdOrObject - Doctor ID string or doctor object with _id/id
 * @param {string|Date|Object} dateOrParams - Date string, Date object, or parameters object
 * @returns {Promise<Object>} Available slots
 */
export const getDoctorAvailability = async (doctorIdOrObject, dateOrParams) => {
  // Extract doctor ID from object or use as string
  let doctorId;
  if (typeof doctorIdOrObject === 'object' && doctorIdOrObject !== null) {
    doctorId = doctorIdOrObject._id || doctorIdOrObject.id;
    if (!doctorId) {
      console.error('Doctor object missing _id and id:', doctorIdOrObject);
    }
  } else {
    doctorId = doctorIdOrObject;
  }

  if (!doctorId) {
    console.error('getDoctorAvailability called with:', doctorIdOrObject);
    throw new Error('Doctor ID is required (received: ' + JSON.stringify(doctorIdOrObject) + ')');
  }

  let url;
  
  if (typeof dateOrParams === 'string') {
    // Single date string
    url = `/api/v1/doctors/${doctorId}/availability?date=${dateOrParams}`;
  } else if (dateOrParams instanceof Date) {
    // Date object, convert to string
    const dateStr = dateOrParams.toISOString().split('T')[0]; // YYYY-MM-DD format
    url = `/api/v1/doctors/${doctorId}/availability?date=${dateStr}`;
  } else if (typeof dateOrParams === 'object' && dateOrParams !== null) {
    // Parameters object with startDate/endDate or date
    const params = new URLSearchParams();
    
    if (dateOrParams.date) {
      const date = dateOrParams.date instanceof Date 
        ? dateOrParams.date.toISOString().split('T')[0]
        : dateOrParams.date;
      params.append('date', date);
    }
    
    if (dateOrParams.startDate) {
      const startDate = dateOrParams.startDate instanceof Date
        ? dateOrParams.startDate.toISOString().split('T')[0]
        : dateOrParams.startDate;
      params.append('startDate', startDate);
    }
    
    if (dateOrParams.endDate) {
      const endDate = dateOrParams.endDate instanceof Date
        ? dateOrParams.endDate.toISOString().split('T')[0]
        : dateOrParams.endDate;
      params.append('endDate', endDate);
    }

    url = `/api/v1/doctors/${doctorId}/availability-blocks?${params.toString()}`;
  } else {
    throw new Error('Invalid date parameter. Expected string, Date object, or parameters object.');
  }
  
  const response = await http.get(url);
  console.log('Fetched availability:', response.data);
  return response.data;
};

/**
 * Get detailed slots for a doctor on a specific date
 * @param {string|Object} doctorIdOrObject - Doctor ID string or doctor object with _id/id
 * @param {string|Date} date - Date in YYYY-MM-DD format or Date object
 * @returns {Promise<Object>} Detailed slots with availability
 */
export const getDoctorSlots = async (doctorIdOrObject, date) => {
  try {
    // Extract doctor ID from object or use as string
    const doctorId = typeof doctorIdOrObject === 'object' && doctorIdOrObject !== null
      ? (doctorIdOrObject._id || doctorIdOrObject.id)
      : doctorIdOrObject;

    if (!doctorId) {
      throw new Error('Doctor ID is required');
    }

    // Ensure date is in proper string format
    const dateStr = date instanceof Date 
      ? date.toISOString().split('T')[0] 
      : date;
      
    const response = await http.get(`/api/v1/doctors/${doctorId}/availability?date=${dateStr}`);
    return {
      success: true,
      data: response.data,
      message: 'Slots retrieved successfully'
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to fetch slots'
    };
  }
};

/**
 * Get availability blocks for a doctor
 * @param {string} doctorId - Doctor ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Availability blocks
 */
export const getDoctorAvailabilityBlocks = async (doctorId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString 
    ? `/api/v1/doctors/${doctorId}/availability-blocks?${queryString}`
    : `/api/v1/doctors/${doctorId}/availability-blocks`;
  
  const response = await http.get(url);
  return response.data;
};

/**
 * Update an availability block
 * @param {string} blockId - Availability block ID
 * @param {Object} data - Updated availability block data
 * @returns {Promise<Object>} Updated availability block
 */
export const updateAvailabilityBlock = async (blockId, data) => {
  try {
    const response = await http.put(`/api/v1/availability-blocks/${blockId}`, data);
    return {
      success: true,
      data: response.data,
      message: 'Availability block updated successfully'
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to update availability block'
    };
  }
};

/**
 * Delete an availability block
 * @param {string} blockId - Availability block ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteAvailabilityBlock = async (blockId) => {
  try {
    const response = await http.delete(`/api/v1/availability-blocks/${blockId}`);
    return {
      success: true,
      data: response.data,
      message: 'Availability block deleted successfully'
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to delete availability block'
    };
  }
};

/**
 * Hold a slot temporarily
 * @param {string} slotId - Slot ID
 * @param {Object} data - Hold data
 * @returns {Promise<Object>} Hold confirmation
 */
export const holdSlot = async (slotId, data = {}) => {
  const response = await http.post(`/api/v1/slots/${slotId}/hold`, data);
  return response.data;
};

/**
 * Close a slot
 * @param {string} slotId - Slot ID
 * @returns {Promise<Object>} Close confirmation
 */
export const closeSlot = async (slotId) => {
  const response = await http.post(`/api/v1/slots/${slotId}/close`);
  return response.data;
};

/**
 * Book a slot
 * @param {string} slotId - Slot ID
 * @param {Object} data - Booking data
 * @returns {Promise<Object>} Booking confirmation
 */
export const bookSlot = async (slotId, data) => {
  const response = await http.post(`/api/v1/slots/${slotId}/book`, data);
  return response.data;
};

/**
 * Get all doctors (for receptionist booking interface)
 * @returns {Promise<Object>} List of doctors
 */
export const getDoctors = async () => {
  const response = await http.get('/api/v1/admin/users?role=Doctor');
  return response.data;
};

/**
 * Search patients by name or mobile
 * @param {string} query - Search query
 * @returns {Promise<Object>} List of patients
 */
export const searchPatients = async (query) => {
  const response = await http.get(`/api/v1/patients/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

/**
 * Add appointment to queue
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise<Object>} Queue confirmation
 */
export const addToQueue = async (appointmentId) => {
  const response = await http.post(`/api/v1/appointments/${appointmentId}/join-queue`);
  return response.data;
};

/**
 * Preview slots before creating availability block
 * @param {Object} data - Availability block data
 * @returns {Object} Preview data with generated slots
 */
export const previewSlots = (data) => {
  const { date, startTime, endTime, slotMinutes, bufferMinutes = 0 } = data;
  
  // Create IST date times (treat input times as IST)
  const startDateTime = new Date(`${date}T${startTime}:00+05:30`);
  const endDateTime = new Date(`${date}T${endTime}:00+05:30`);
  const totalMinutes = (endDateTime - startDateTime) / (1000 * 60);
  
  if (totalMinutes <= 0) {
    return {
      success: false,
      message: 'End time must be after start time',
      slots: []
    };
  }
  
  // Each slot duration + buffer time
  const slotInterval = slotMinutes + bufferMinutes;
  
  if (slotInterval <= 0) {
    return {
      success: false,
      message: 'Slot duration must be greater than 0',
      slots: []
    };
  }
  
  try {
    const slots = [];
    let currentTime = new Date(startDateTime);
    let guard = 0;
    
    while (currentTime < endDateTime) {
      const slotStartTime = new Date(currentTime);
      const slotEndTime = new Date(currentTime.getTime() + slotMinutes * 60000);
      
      // Stop when the slot itself would overflow the configured end time
      if (slotEndTime > endDateTime) {
        break;
      }
      
      // Display IST times in preview (input times are already in IST)
      const startHours = slotStartTime.getHours().toString().padStart(2, '0');
      const startMinutes = slotStartTime.getMinutes().toString().padStart(2, '0');
      const endHours = slotEndTime.getHours().toString().padStart(2, '0');
      const endMinutes = slotEndTime.getMinutes().toString().padStart(2, '0');
      
      slots.push({
        startTime: `${startHours}:${startMinutes}`,
        endTime: `${endHours}:${endMinutes}`,
        duration: slotMinutes,
        bufferMinutes: bufferMinutes
      });
      
      // Move to next slot start time (current slot + buffer time)
      currentTime = new Date(slotEndTime.getTime() + bufferMinutes * 60000);
      guard += 1;
      if (guard > 2000) {
        console.warn('Preview slot generation aborted: exceeded guard limit');
        break;
      }
    }
    
    if (slots.length === 0) {
      return {
        success: false,
        message: `Cannot fit any slots of ${slotMinutes} minutes within the available ${totalMinutes} minute window`,
        slots: []
      };
    }
    
    return {
      success: true,
      slots,
      totalSlots: slots.length,
      totalDuration: totalMinutes,
      slotInterval: slotInterval,
      bufferMinutes: bufferMinutes
    };
    
  } catch (error) {
    return {
      success: false,
      message: error.message,
      slots: []
    };
  }
};

// Export default object for easier imports in calendar components
const availabilityAPI = {
  createAvailabilityBlock,
  updateAvailabilityBlock,
  deleteAvailabilityBlock,
  getDoctorAvailability,
  getDoctorSlots,
  getDoctorAvailabilityBlocks,
  holdSlot,
  closeSlot,
  bookSlot,
  getDoctors,
  searchPatients,
  addToQueue,
  previewSlots
};

export default availabilityAPI;
